import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
} from "@mui/material";
import AnalyticsCard from "./AnalyticsCard";
import AVG_SPEED_ICON from "../../images/feet/average.svg";
import RUNTIME_ICON from "../../images/feet/runtime.svg";
import DISTANCE_ICON from "../../images/feet/distance.svg";
import IDLE_TIME from "../../images/feet/idle.svg";
import DashboardAnalyticsChart from "../manageData/Analytics/DashboardAnalyticsChart";
import { useProgressiveOverviewData } from "../manageData/Analytics/useProgressiveOverviewData";
import NormalArrowFunction from "../common/NormalArrowFunction.jsx";
import { useTranslation } from "react-i18next";

// Helper function to parse time strings like "8h 30m 15s" to hours
const parseTimeStringToHours = (timeStr) => {
  if (!timeStr) return 0;

  const hoursMatch = timeStr.match(/(\d+)h/);
  const minutesMatch = timeStr.match(/(\d+)m/);
  const secondsMatch = timeStr.match(/(\d+)s/);

  const hours = hoursMatch ? parseInt(hoursMatch[1]) : 0;
  const minutes = minutesMatch ? parseInt(minutesMatch[1]) : 0;
  const seconds = secondsMatch ? parseInt(secondsMatch[1]) : 0;

  return hours + minutes / 60 + seconds / 3600;
};

const PerformanceAnalytics = ({ vehiclesData }) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState("today");
  const analyticsRef = useRef(null);
  const observerRef = useRef(null); // Add this ref to store the observer instance

  const customDateRange = null;

  const availableDuration = [
    { id: "today", title: t("todayLabel", "Today") },
    { id: "7days", title: t("last7Days", "7 Days") },
    { id: "30days", title: t("last30Days", "30 Days") },
    { id: "90days", title: t("last90Days", "90 Days") },
  ];

  const handleChartSelection = (event) => {
    setSelectedDateRange(event.target.value);
  };

  // Only fetch data when component is visible
  const { chartDataStates, loadingStates, error } = useProgressiveOverviewData(
    isVisible ? selectedDateRange : null,
    customDateRange,
  );

  // Set up intersection observer with proper cleanup
  useEffect(() => {
    const currentRef = analyticsRef.current; // Store ref in local variable

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Disconnect once visible to prevent re-triggering
          if (observerRef.current) {
            observerRef.current.disconnect();
          }
        }
      },
      {
        root: null,
        rootMargin: "100px", // Start loading slightly before visible
        threshold: 0.1,
      },
    );

    if (currentRef && observerRef.current) {
      observerRef.current.observe(currentRef);
    }

    // CRITICAL: Use local variable in cleanup
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  // Calculate analytics from real data
  const analyticsData = useMemo(() => {
    const activityData = chartDataStates["activity_vehicles"]?.data;
    const runtimeData = chartDataStates["runtime_vehicles"]?.data;
    const haltData = chartDataStates["halt_vehicles"]?.data;

    // Calculate Average Speed
    let avgSpeed = 0;
    if (activityData?.data && Array.isArray(activityData.data)) {
      const speeds = activityData.data
        .map((v) => parseFloat(v.averageSpeed))
        .filter((s) => s > 0);
      avgSpeed =
        speeds.length > 0
          ? (speeds.reduce((sum, s) => sum + s, 0) / speeds.length).toFixed(1)
          : 0;
    }

    // Calculate Total Runtime
    let totalRuntime = 0;
    if (runtimeData?.data && Array.isArray(runtimeData.data)) {
      totalRuntime = runtimeData.data
        .reduce((sum, v) => sum + (parseFloat(v.totalMovingHours) || 0), 0)
        .toFixed(1);
    }

    // Calculate Total Distance
    let totalDistance = 0;
    if (runtimeData?.data && Array.isArray(runtimeData.data)) {
      totalDistance = runtimeData.data
        .reduce((sum, v) => sum + (parseFloat(v.noOfKmsCovered) || 0), 0)
        .toFixed(0);
    }

    // FIXED: Calculate Idle Time - Only INACTIVE halts, not all halts
    let idleTime = 0;
    if (haltData?.devices && Array.isArray(haltData.devices)) {
      haltData.devices.forEach((device) => {
        // Only count inactive time, not dead halts
        const timeStr =
          device.totalInactiveTime?.string ||
          device.inactiveTime?.string ||
          device.deadTime?.string ||
          "0h 0m 0s";
        idleTime += parseTimeStringToHours(timeStr);
      });
      idleTime = idleTime.toFixed(1);
    } else if (haltData?.data && Array.isArray(haltData.data)) {
      // Alternative: if data comes in different format
      haltData.data.forEach((halt) => {
        const type = (halt.type || halt.haltType || "").toLowerCase();
        if (type.includes("inactive") || type.includes("dead")) {
          const duration = parseTimeStringToHours(halt.duration || "0h 0m 0s");
          idleTime += duration;
        }
      });
      idleTime = idleTime.toFixed(1);
    }

    return [
      {
        title: t("averageSpeed", "Average Speed"),
        value: avgSpeed > 0 ? `${avgSpeed} km/h` : "0 km/h",
        icon: AVG_SPEED_ICON,
        changeValue: null,
        changeColor: "#008C23",
        loading: loadingStates["activity_vehicles"]?.loading,
      },
      {
        title: t("totalRuntime", "Total Runtime"),
        value: totalRuntime > 0 ? `${totalRuntime}hrs` : "0hrs",
        icon: RUNTIME_ICON,
        changeValue: null,
        changeColor: "#FF4444",
        loading: loadingStates["runtime_vehicles"]?.loading,
      },
      {
        title: t("totalDistance", "Total Distance"),
        value:
          totalDistance > 0
            ? `${Number(totalDistance).toLocaleString()} km`
            : "0 km",
        icon: DISTANCE_ICON,
        changeValue: null,
        changeColor: "#008C23",
        loading: loadingStates["runtime_vehicles"]?.loading,
      },
      {
        title: t("idleTime", "Idle Time"), // This is INACTIVE halt time only
        value: idleTime > 0 ? `${idleTime}h` : "0h",
        icon: IDLE_TIME,
        changeValue: null,
        changeColor: "#FF4444",
        loading: loadingStates["halt_vehicles"]?.loading,
      },
    ];
  }, [chartDataStates, loadingStates, t]);

  return (
    <Box
      ref={analyticsRef}
      sx={{
        p: { xs: 2, sm: 3 },
        mx: "auto",
        boxShadow: "-2px 2px 5px 0px #0000001a",
        borderRadius: "8px",
        margin: { xs: "0px 16px", sm: "0px 24px", md: "0px 40px" },
        overflowX: "hidden",
      }}
    >
      <Typography
        variant="h4"
        component="div"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          fontWeight: 600,
          fontSize: { xs: "18px", sm: "24px" },
          color: "#FFFFFF",
          background: "#647490",
          boxShadow: "-2px 2px 5px 0px #00000040",
          minHeight: "63px",
          height: "auto",
          borderRadius: "12px",
          padding: { xs: "12px", sm: "10px 20px" },
          mb: 3,
          fontFamily: "Roboto",
          gap: { xs: 1.5, sm: 2 },
        }}
      >
        {t("fleetAnalytics", "Fleet Analytics")}
        <FormControl
          size="small"
          sx={{
            minWidth: { xs: 0, sm: 180, md: 250 },
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <Select
            IconComponent={NormalArrowFunction}
            sx={{
              border: "1px solid #183059",
              backgroundColor: "#FFFFFF",
              width: { xs: "100%", sm: 160 },
              height: 43,
              borderRadius: "12px",
              paddingTop: "12px",
              paddingRight: "24px",
              paddingBottom: "12px",
              paddingLeft: "24px",
              borderWidth: "1px",
              "&": {
                cursor: "pointer",
                userSelect: "none",
              },
              "& .MuiSelect-select": {
                cursor: "pointer",
                userSelect: "none",
              },
              "& .MuiSelect-select *": {
                cursor: "pointer",
                userSelect: "none",
              },
            }}
            displayEmpty
            value={selectedDateRange}
            onChange={handleChartSelection}
            renderValue={(selected) => {
              const selectedOption = availableDuration.find(
                (option) => option.id === selected,
              );
              return <em>{selectedOption?.title || t("todayLabel", "Today")}</em>;
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 300,
                  "& .MuiMenuItem-root": {
                    fontSize: "14px",
                    color: "#1F242E",
                  },
                },
              },
            }}
          >
            {availableDuration.map((duration) => (
              <MenuItem key={duration.id} value={duration.id}>
                <ListItemText primary={duration.title} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Typography>

      {/* Show loading state while not visible */}
      {!isVisible ? (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">
            {t("loadingAnalytics", "Loading analytics...")}
          </Typography>
        </Box>
      ) : (
        <>
          {error && (
            <Box sx={{ mb: 2, p: 2, bgcolor: "#ffebee", borderRadius: 1 }}>
              <Typography color="error">
                {t("errorLoadingAnalytics", "Error loading analytics")}: {error}
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              "@media (max-width: 768px)": {
                flexDirection: "column",
              },
            }}
          >
            {analyticsData.map((card, index) => (
              <AnalyticsCard key={`${card.title}-${index}`} {...card} />
            ))}
          </Box>

          <DashboardAnalyticsChart
            vehiclesData={vehiclesData}
            selectedDateRange={selectedDateRange}
          />
        </>
      )}
    </Box>
  );
};

export default PerformanceAnalytics;
